// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { MyAccountPageContext } from "@insite/content-library/Pages/MyAccountPage";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadAccountTiles from "@mcmc/App/Handlers/MyAccount/LoadAccountTiles";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getAccountTiles } from "@mcmc/App/Store/Selectors/AccountTilesSelectors";
import Tooltip, { TooltipPresentationProps } from "@mcmc/MobiusOverrides/Tooltip";
import { styledBox } from "@mcmc/XDL/Components/Common/Box";
import { Roboto } from "@mcmc/XDL/Utilities";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { LinkProps } from "react-router-dom";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: McMcApplicationState) => ({
    accountTiles: getAccountTiles(state),
    orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
});

const mapDispatchToProps = {
    loadAccountTiles,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export interface AccountTilesStyles {
    accountTilesGridContainer: GridContainerProps;
    accountTilesGridItem: GridItemProps;
    accountTilesGridItemContainer: InjectableCss;
    statNumber: TypographyProps;
    statTitle: TypographyProps;
    viewLink: LinkPresentationProps;
    podTooltip?: TooltipPresentationProps;
}

export const accountTilesStyles: AccountTilesStyles = {
    accountTilesGridContainer: {
        gap: 30,
    },
    accountTilesGridItem: {
        width: [12, 4, 4, 4, 4],
    },
    accountTilesGridItemContainer: {
        css: css`
            ${styledBox};
            width: 100%;
            height: 100%;
            padding: 20px 10px;
            margin-bottom: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
        `,
    },
    statNumber: {
        variant: "p",
        css: css`
            text-align: center;
            margin-bottom: 5px;
        `,
        weight: 700,
    },
    statTitle: {
        variant: "h6",
        css: css`
            text-align: center;
            margin-bottom: 0;
        `,
    },
    viewLink: {
        typographyProps: { ...Roboto, weight: 500 },
        css: css`
            ${getProp("theme.link.defaultProps.css")}
            text-align: center;
            display: block;
            font-size: 16px;
            margin-top: auto;
        `,
    },
    podTooltip: {
        cssOverrides: {
            tooltipClickable: css`
                margin-left: 5px;
            `,
        },
    },
};

const styles = accountTilesStyles;

const AccountTilesWidget: React.FC<Props> = ({ accountTiles, orderHistoryPageLink, loadAccountTiles }) => {
    useEffect(() => {
        if (!accountTiles) {
            loadAccountTiles({});
        }
    }, []);

    const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

    return (
        <>
            {accountTiles && (
                <GridContainer {...styles.accountTilesGridContainer}>
                    <GridItem {...styles.accountTilesGridItem}>
                        <StyledWrapper {...styles.accountTilesGridItemContainer}>
                            <Typography {...styles.statNumber} className="hero-small">
                                {accountTiles.openOrdersCount}
                            </Typography>
                            <Typography {...styles.statTitle}>{translate("Open Orders")}</Typography>
                            <Link
                                {...styles.viewLink}
                                href={`${orderHistoryUrl}?orderType=open&customerSequence=-1`}
                                aria-label={translate("View Open Orders")}
                            >
                                {translate("View")}
                            </Link>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.accountTilesGridItem}>
                        <StyledWrapper {...styles.accountTilesGridItemContainer}>
                            <Typography {...styles.statNumber} className="hero-small">
                                {accountTiles.ordersInTransitCount}
                            </Typography>
                            <Typography {...styles.statTitle}>{translate("Orders in Transit")}</Typography>
                            <Link
                                {...styles.viewLink}
                                href={`${orderHistoryUrl}?orderType=intransit&customerSequence=-1`}
                                aria-label={translate("View Orders in Transit")}
                            >
                                {translate("View")}
                            </Link>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.accountTilesGridItem}>
                        <StyledWrapper {...styles.accountTilesGridItemContainer}>
                            <Typography {...styles.statNumber} className="hero-small">
                                {accountTiles.proofOfDeliveryCount}
                            </Typography>
                            <Typography {...styles.statTitle}>
                                {translate("Proof of Delivery")}
                                <Tooltip
                                    text={siteMessage("MCMC_POD_DashboardToolTip") as string}
                                    appendTo={() => document.body}
                                    {...styles.podTooltip}
                                />
                            </Typography>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            )}
        </>
    );
};

const accountTilesWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountTilesWidget),
    definition: {
        group: "My Account",
        allowedContexts: [MyAccountPageContext],
    },
};

export default accountTilesWidget;
